// Step2_LocationInstitution.jsx
import { useCallback, useEffect, useState } from "react";
import { useFormikContext } from "formik";
import SelectInput from "@components/shared/SelectInput";
import TextInput from "@/components/shared/TextInput";
import StudentSelectInput from "@/components/shared/StudentSelectInputRegister";
import OrganizationSelectInput from "@/components/shared/OrganizationSelectInputRegister";
import CollegeSelectInput from "@/components/shared/CollegeSelectInputRegister";
import { BASE_URL } from "@/utils/constants";
import { useNavigate } from "react-router-dom";

export default function Step2_LocationInstitution({ values, errors, touched, setFieldValue }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [genders, setGenders] = useState([
    { text: "MALE", id: 'M' },
    { text: "FEMALE", id: 'F' },
    { text: "OTHERS", id: 'O' },
  ]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [choosenOptions, setChoosenOptions] = useState({
    state: null,
    org: null,
    colleges: null
  });

  useEffect(() => {
    getStates();
  }, [])

  const getStates = async () => {
    try {
      setLoading(true);
      const response = await fetch(BASE_URL + "api/user/register/states", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        }
      }, navigate);
      const data = await response.json();
      setStates(data.data || []);
    } catch (error) {
      console.error("Failed to fetch states:", error);
      setRegistrationError("Failed to load states. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getCities = useCallback(async (stateId, keyword = "") => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BASE_URL}api/user/register/cities?stateId=${stateId}&limit=50&searchKeyword=${keyword}`,
        { method: "GET", headers: { "Content-Type": "application/json" } },
        navigate
      );
      const data = await response.json();
      setCities(data.data || []);
    } catch (error) {
      console.error("Failed to fetch cities:", error);
      setRegistrationError("Failed to load cities. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, navigate]);

  const getOrgs = useCallback(async (stateId, keyword = "") => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BASE_URL}api/user/register/organizations?stateId=${stateId}&limit=50&searchKeyword=${keyword}`,
        { method: "GET", headers: { "Content-Type": "application/json" } },
        navigate
      );
      const data = await response.json();
      setOrgs(data.data || []);
    } catch (error) {
      console.error("Failed to fetch orgs:", error);
      setRegistrationError("Failed to load organizations. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, navigate]);


  const getColleges = useCallback(async (orgId, searchKeyword = "") => {
    try {
      setLoading(true);
      const response = await fetch(BASE_URL + `api/user/register/colleges?organizationId=${orgId}&limit&searchKeyword=${searchKeyword}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        }
      }, navigate);
      const data = await response.json();
      setColleges(data.data || []);
    } catch (error) {
      console.error("Failed to fetch colleges:", error);
      setRegistrationError("Failed to load colleges. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, navigate]);

  const handleCollegeSearch = useCallback(
    (keyword) => {
      if (choosenOptions.org) {
        getColleges(choosenOptions.org, keyword);
      }
    },
    [choosenOptions.org, getColleges] // only re-create if these change
  );

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-5">
        <TextInput
          label="First Name"
          name="firstName"
          type="text"
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
          labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base"
          errorClassName="text-red-600 font-['Semantic/Strong'] text-sm"
          required={true}
        />
        <TextInput
          label="Middle Name"
          name="middleName"
          type="text"
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
          labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base"
          errorClassName="text-red-600 font-['Semantic/Strong'] text-sm"
        />
        <TextInput
          label="Last Name"
          name="lastName"
          type="text"
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
          labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base"
          errorClassName="text-red-600 font-['Semantic/Strong'] text-sm"
          required={true}
        />
        <SelectInput
          label="Select State"
          name="state"
          className="px-4 py-2 border border-gray-300 rounded-md font-['Semantic/Strong'] text-gray-700"
          labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base"
          errorClassName="text-red-600 font-['Semantic/Strong'] text-sm"
          onChange={(e) => {
            const selectedStateName = Number(e.target.value);
            console.log("Selected State Name:", selectedStateName);
            console.log("Available States:", states);
            const selectedState = states.find((s) => s.stateId === selectedStateName);
            console.log("Selected State Object:", selectedState);

            // Set state in Formik
            setFieldValue("state", selectedStateName);

            if (selectedState) {
              // Record stateId in choosenOptions
              setChoosenOptions((prev) => ({
                ...prev,
                state: selectedState.stateId,
              }));

              // Reset dependent dropdowns
              setCities([]);
              setOrgs([]);
              setColleges([]);
              setFieldValue("city", "");
              setFieldValue("organization", "");
              setFieldValue("college", "");

              // Fetch dependent data
              getCities(selectedState.stateId);
              getOrgs(selectedState.stateId);
            }
          }}
        >
          <option value="" className="text-gray-600 text-[14px]">Select State</option>
          {states?.length ? (
            states.map((g) => (
              <option key={g.stateId} value={g.stateId} className="text-gray-600 text-[14px]">
                {g.stateName}
              </option>
            ))
          ) : (
            <option value="" disabled>
              No states available
            </option>
          )}
        </SelectInput>

        <StudentSelectInput
          label="Select City"
          data={cities}
          name="city"
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700"
          labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base"
          errorClassName="text-red-600 font-['Semantic/Strong'] text-sm"
          onSearch={(keyword) => {
            if (choosenOptions.state) {
              getCities(choosenOptions.state, keyword);
            }
          }}
        >
          <option value="">Select City</option>
          {cities?.length ? (
            cities.map((g) => (
              <option key={g.cityId} value={g.cityId}>
                {g.cityName}
              </option>
            ))
          ) : (
            <option value="" disabled>
              No cities available
            </option>
          )}
        </StudentSelectInput>

        <OrganizationSelectInput
          label="Select Organization"
          name="organization"
          data={orgs}
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700"
          labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base"
          errorClassName="text-red-600 font-['Semantic/Strong'] text-sm"
          onChange={(e) => {
            const selectedOrgName = Number(e.target.value);
            console.log("Selected Organization Name:", selectedOrgName);
            console.log("Available Organizations:", orgs);
            const selectedOrg = orgs.find((o) => o.organizationId === selectedOrgName);
            console.log("Selected Organization Object:", selectedOrg);

            // Update Formik value
            setFieldValue("org", selectedOrgName);

            if (selectedOrg) {
              // Store selected orgId in choosenOptions
              setChoosenOptions((prev) => ({
                ...prev,
                org: selectedOrg.organizationId,
              }));

              // Reset dependent dropdown
              setColleges([]);
              setFieldValue("college", "");

              // Fetch dependent colleges
              getColleges(selectedOrg.organizationId);
            }
          }}
          onSearch={(keyword) => {
            if (choosenOptions.state) {
              getOrgs(choosenOptions.state, keyword);
            }
          }}
        >
          <option value="">Select Organization</option>
          {orgs?.length ? (
            orgs.map((g) => (
              <option key={g.organizationId} value={g.organizationId}>
                {g.organizationName}
              </option>
            ))
          ) : (
            <option value="" disabled>
              No organizations available
            </option>
          )}
        </OrganizationSelectInput>

        <CollegeSelectInput
          label="Select College"
          data={colleges}
          name="college"
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700"
          labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base"
          errorClassName="text-red-600 font-['Semantic/Strong'] text-sm"
          onChange={(e) => {
            const selectedCollegeName = e.target.value;
            console.log("Selected College Name:", selectedCollegeName);
            const selectedCollege = colleges.find(
              (c) => c.collegeId === selectedCollegeName
            );
            console.log("Selected College Object:", selectedCollege);
            setFieldValue("college", selectedCollege.collegeId);
          }}
          onSearch={handleCollegeSearch}
        >
          <option value="">Select College</option>
          {colleges?.length ? (
            colleges.map((g) => (
              <option key={g.collegeId} value={g.collegeId}>
                {g.collegeName}
              </option>
            ))
          ) : (
            <option value="" disabled>
              No colleges available
            </option>
          )}
        </CollegeSelectInput>
      </div>
    </div>
  );
}